import { SocialDetailRow } from '../clients/SocialFields';
import { useSocialFields } from '../../lib/hooks/useSocialFields';
import React, { useEffect, useState } from 'react';
import {
  AtSign, Briefcase, Building2, Calendar, ChevronDown, ChevronRight, Copy,
  CreditCard, ExternalLink, FileText, Globe, Hash, Mail, MapPin,
  MessageCircle, MessageSquare, Percent, Phone, PhoneCall, Truck,
} from 'lucide-react';
import { useWarehouseSettings } from '../../lib/hooks/useWarehouseSettings';
import { getVatCode, vatCodeLabel } from '../../lib/services/vatCodeService';
import { searchForDuplicates } from '../../lib/services/duplicateDetectionService';
import { useApp } from '../AppProvider';
import { ClickToCallButton } from '../shared/ClickToCallButton';
import { InlineTextRow, PanelSection, ValueRow } from '../shared/property-rows';
import { VoiceCallCard } from '../voice/VoiceCallCard';
import { regionDisplayName } from '../../lib/utils/italianRegions';

interface LeadInfoRowsProps {
  selectedClient: any;
  canEdit: boolean;
  onLeadUpdate: (id: string, updates: any) => Promise<boolean>;
  handleCopy: (value: string, label: string) => void;
  /** Apre la chat WhatsApp embedded (solo desktop, se configurata). */
  /** Apre la chat WhatsApp del lead; riceve il rettangolo del pulsante per ancorare la scelta del numero. */
  onOpenWhatsApp?: (anchor: DOMRect, phone: string) => void;
  /** Apre il wizard di modifica (campi composti: indirizzi, tipo, nascita). */
  onEditWizard?: () => void;
  /** Riga Chiamate AI (org beta + permesso voice_agents). */
  showVoiceRow?: boolean;
  canDialVoice?: boolean;
  title?: string;
  /** Stato iniziale della sezione. Default true (comportamento storico/desktop). */
  defaultOpen?: boolean;
}

const rowIcon = (Icon: React.ComponentType<any>) => <Icon className="h-3 w-3" strokeWidth={1.5} />;

/** Bottone azione ghost nel trailing della riga (non propaga il click alla riga). */
function RowAction({ title, onClick, children }: { title: string; onClick: (anchor: DOMRect) => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      title={title}
      onClick={(e) => { e.stopPropagation(); onClick(e.currentTarget.getBoundingClientRect()); }}
      className="rounded p-0.5 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.08]"
    >
      {children}
    </button>
  );
}

const joinAddress = (parts: (string | null | undefined)[]) => parts.filter(Boolean).join(', ');

/** Campi il cui valore, se gia' presente, si sostituisce solo dopo conferma. */
const ETICHETTE_RECAPITI: Record<string, string> = {
  email: "l'email",
  pec: 'la PEC',
  phone: 'il telefono',
  mobile: 'il cellulare',
  whatsapp_number: 'il numero WhatsApp',
};

/**
 * Blocco "Informazioni" del pannello dettagli: i campi decisivi sempre visibili
 * con edit inline a salvataggio immediato, il resto dietro la riga ghost
 * "Mostra tutto (N campi)". Copia/chiama/apri chat all'hover; i campi composti
 * (indirizzi, tipo, data di nascita) restano in sola lettura → wizard.
 */
export function LeadInfoRows({
  selectedClient, canEdit, onLeadUpdate, handleCopy, onOpenWhatsApp,
  onEditWizard, showVoiceRow, canDialVoice, title = 'Informazioni',
  defaultOpen = true,
}: LeadInfoRowsProps) {
  const { toast, confirm } = useApp();
  const [showAll, setShowAll] = useState(() => sessionStorage.getItem('lead-info-show-all') === '1');
  const [voiceOpen, setVoiceOpen] = useState(false);
  const { fields: socialFields } = useSocialFields();
  // Codice IVA del cliente (esenzione): solo org con «Codici IVA dei clienti» attivo
  const { client_vat_codes_enabled: vatCodesEnabled } = useWarehouseSettings();
  const vatCodeId: string | null = selectedClient.vat_code_id || null;
  const [vatCodeText, setVatCodeText] = useState<string | null>(null);
  useEffect(() => {
    setVatCodeText(null);
    if (!vatCodesEnabled || !vatCodeId) return;
    let cancelled = false;
    getVatCode(vatCodeId).then((c) => { if (!cancelled && c) setVatCodeText(vatCodeLabel(c)); });
    return () => { cancelled = true; };
  }, [vatCodesEnabled, vatCodeId]);

  const toggleShowAll = () => {
    setShowAll((v) => {
      sessionStorage.setItem('lead-info-show-all', v ? '0' : '1');
      return !v;
    });
  };

  const saveField = (field: string) => async (value: string | null): Promise<boolean> => {
    // I recapiti si sovrascrivono solo dopo conferma: un click di troppo sulla riga
    // cancellava un numero buono (riunione commerciale del 21/09/2026). Se il campo
    // era vuoto non si chiede nulla.
    const etichetta = ETICHETTE_RECAPITI[field];
    const attuale = (selectedClient as Record<string, unknown>)[field];
    if (etichetta && typeof attuale === 'string' && attuale.trim() && (value ?? '') !== attuale) {
      const confermato = await confirm({
        title: `Sostituire ${etichetta}?`,
        message: value?.trim()
          ? `Da "${attuale}" a "${value}". Il valore precedente va perso.`
          : `"${attuale}" verra' cancellato.`,
        confirmLabel: 'Sostituisci',
        cancelLabel: 'Annulla',
        type: 'danger',
      });
      if (!confermato) return false;
    }
    const ok = await onLeadUpdate(selectedClient.id, { [field]: value });
    if (ok) toast.success('Campo aggiornato');
    else toast.error("Errore durante l'aggiornamento");
    if (ok && value?.trim() && (field === 'email' || field === 'phone' || field === 'vat_number')) {
      warnDuplicates(field, value);
    }
    return ok;
  };

  // Doppioni dopo una modifica inline di email/telefono/P.IVA: solo avviso, il valore resta salvato
  const warnDuplicates = (field: 'email' | 'phone' | 'vat_number', value: string) => {
    searchForDuplicates({ [field]: value }, { excludeClientId: selectedClient.id })
      .then((res) => {
        if (!res.has_duplicates) return;
        const names = [
          ...res.duplicates.map((d: any) => d.business_name || d.contact_name || d.email),
          ...res.others.map((o) => `${o.kind} ${o.name}`),
        ].filter(Boolean);
        toast.warning(`Già presente in: ${names.slice(0, 3).join('; ')}${names.length > 3 ? ` e altri ${names.length - 3}` : ''}`);
      })
      .catch((error) => console.error('Error checking for duplicates:', error));
  };

  const copyAction = (value: string | null | undefined, label: string) =>
    value ? (
      <RowAction title={`Copia ${label}`} onClick={() => handleCopy(value, label)}>
        <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
      </RowAction>
    ) : undefined;

  const whatsappValue = selectedClient.whatsapp_number || selectedClient.mobile || null;
  const whatsappField = selectedClient.whatsapp_number
    ? 'whatsapp_number'
    : selectedClient.mobile
      ? 'mobile'
      : 'whatsapp_number';

  // WhatsApp vuoto ma telefono presente: un click lo copia (prima andava ricopiato a mano)
  const canCopyPhoneToWhatsApp = canEdit && !whatsappValue && !!selectedClient.phone;

  const address = joinAddress([
    selectedClient.address || selectedClient.street_address,
    selectedClient.postal_code,
    selectedClient.city,
    selectedClient.province ? `(${selectedClient.province})` : null,
    regionDisplayName(selectedClient.region),
    selectedClient.country,
  ]);

  const operationalAddress = selectedClient.has_operational_address
    ? joinAddress([
        selectedClient.operational_address,
        selectedClient.operational_postal_code,
        selectedClient.operational_city,
        selectedClient.operational_province ? `(${selectedClient.operational_province})` : null,
        selectedClient.operational_region,
        selectedClient.operational_country,
      ])
    : '';

  const activeLocations = (selectedClient.operational_locations || []).filter((l: any) => l.is_active);
  // La sede operativa storica (campi operational_*) è stata copiata in tabella
  // dalla migration 20260903: se c'è già una riga uguale non la mostro due volte
  const norm = (v: any) => String(v || '').trim().toLowerCase();
  const legacyDuplicated = activeLocations.some((l: any) =>
    norm(l.address) === norm(selectedClient.operational_address) && norm(l.city) === norm(selectedClient.operational_city));

  // Campi "dietro" il Mostra tutto: solo quelli valorizzati generano una riga
  const hiddenFields: { key: string; render: () => React.ReactNode }[] = [];
  const push = (key: string, condition: any, render: () => React.ReactNode) => {
    if (condition) hiddenFields.push({ key, render });
  };

  push('pec', selectedClient.pec, () => (
    <InlineTextRow icon={rowIcon(AtSign)} label="PEC" value={selectedClient.pec} isLink
      onSave={saveField('pec')} canEdit={canEdit} actions={copyAction(selectedClient.pec, 'PEC')} />
  ));
  push('entity_type', selectedClient.entity_type, () => (
    <ValueRow icon={rowIcon(Building2)} label="Tipo"
      value={selectedClient.entity_type === 'company' ? 'Azienda' : 'Persona Fisica'} onClick={onEditWizard} />
  ));
  push('trade_name', selectedClient.trade_name, () => (
    <InlineTextRow icon={rowIcon(Building2)} label="Insegna" value={selectedClient.trade_name}
      onSave={saveField('trade_name')} canEdit={canEdit} actions={copyAction(selectedClient.trade_name, 'Insegna')} />
  ));
  push('client_code', selectedClient.client_code, () => (
    <InlineTextRow icon={rowIcon(Hash)} label="Codice cliente" value={selectedClient.client_code}
      onSave={saveField('client_code')} canEdit={canEdit} actions={copyAction(selectedClient.client_code, 'Codice cliente')} />
  ));
  push('birth_date', selectedClient.birth_date, () => (
    <ValueRow icon={rowIcon(Calendar)} label="Data di nascita"
      value={new Date(selectedClient.birth_date).toLocaleDateString('it-IT')} onClick={onEditWizard} />
  ));
  push('fiscal_code', selectedClient.fiscal_code || selectedClient.tax_code, () => (
    <InlineTextRow icon={rowIcon(FileText)} label="Codice fiscale"
      value={selectedClient.fiscal_code || selectedClient.tax_code}
      onSave={saveField('fiscal_code')} canEdit={canEdit}
      actions={copyAction(selectedClient.fiscal_code || selectedClient.tax_code, 'Codice fiscale')} />
  ));
  push('sdi_code', selectedClient.sdi_code, () => (
    <InlineTextRow icon={rowIcon(Hash)} label="Codice SDI" value={selectedClient.sdi_code}
      onSave={saveField('sdi_code')} canEdit={canEdit} actions={copyAction(selectedClient.sdi_code, 'Codice SDI')} />
  ));
  push('vat_code', vatCodeText, () => (
    <ValueRow icon={rowIcon(Percent)} label="Codice IVA" value={vatCodeText} onClick={onEditWizard} />
  ));
  push('ateco_code', selectedClient.ateco_code, () => (
    <InlineTextRow icon={rowIcon(Briefcase)} label="Codice ATECO" value={selectedClient.ateco_code}
      onSave={saveField('ateco_code')} canEdit={canEdit} actions={copyAction(selectedClient.ateco_code, 'Codice ATECO')} />
  ));
  push('operational_address', operationalAddress && !legacyDuplicated, () => (
    <ValueRow icon={rowIcon(MapPin)} label="Sede operativa" value={operationalAddress}
      onClick={() => handleCopy(operationalAddress, 'Sede operativa')}
      actions={copyAction(operationalAddress, 'Sede operativa')} />
  ));
  activeLocations.forEach((location: any) => {
    const locAddress = joinAddress([
      location.address, location.postal_code, location.city,
      location.province ? `(${location.province})` : null, regionDisplayName(location.region), location.country,
    ]);
    push(`loc-${location.id}`, true, () => (
      <ValueRow key={location.id} icon={rowIcon(location.location_type === 'shipping' ? Truck : Building2)} label={location.name}
        value={locAddress || 'Indirizzo non specificato'}
        onClick={() => locAddress && handleCopy(locAddress, location.name)}
        actions={copyAction(locAddress, location.name)} />
    ));
  });

  // Social: non sono campi primari, stanno dietro "Mostra tutto" dopo tutti gli altri
  // (anche vuoti, cosi' si compilano da qui senza passare dal wizard)
  socialFields.filter((f) => f.is_visible).forEach((f) => {
    push(`social:${f.field_key}`, true, () => (
      <SocialDetailRow field={f} client={selectedClient} canEdit={canEdit}
        onSave={(updates) => onLeadUpdate(selectedClient.id, updates)} />
    ));
  });

  return (
    <PanelSection
      title={title}
      defaultOpen={defaultOpen}
      action={canEdit && onEditWizard ? { label: 'Modifica', onClick: onEditWizard } : undefined}
    >
      <InlineTextRow
        icon={rowIcon(Mail)} label="Email" type="email" value={selectedClient.email} isLink
        onSave={saveField('email')} canEdit={canEdit}
        actions={
          <>
            {selectedClient.email && (
              <RowAction title="Scrivi email" onClick={() => { window.location.href = `mailto:${selectedClient.email}`; }}>
                <Mail className="h-3.5 w-3.5" strokeWidth={1.5} />
              </RowAction>
            )}
            {copyAction(selectedClient.email, 'Email')}
          </>
        }
      />

      <InlineTextRow
        icon={rowIcon(Phone)} label="Telefono" value={selectedClient.phone}
        onSave={saveField('phone')} canEdit={canEdit}
        actions={
          <>
            {selectedClient.phone && (
              <span onClick={(e) => e.stopPropagation()}>
                <ClickToCallButton phoneNumber={selectedClient.phone} size="sm" />
              </span>
            )}
            {copyAction(selectedClient.phone, 'Telefono')}
          </>
        }
      />

      <InlineTextRow
        icon={rowIcon(MessageSquare)} label="WhatsApp" value={whatsappValue}
        onSave={saveField(whatsappField)} canEdit={canEdit}
        actions={
          <>
            {whatsappValue && onOpenWhatsApp && (
              <RowAction title="Apri chat WhatsApp" onClick={(anchor) => onOpenWhatsApp(anchor, whatsappValue)}>
                <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.5} />
              </RowAction>
            )}
            {whatsappValue && (
              <span onClick={(e) => e.stopPropagation()}>
                <ClickToCallButton phoneNumber={whatsappValue} size="sm" />
              </span>
            )}
            {copyAction(whatsappValue, 'WhatsApp')}
            {canCopyPhoneToWhatsApp && (
              <RowAction title="Usa lo stesso numero del telefono" onClick={() => { void saveField('whatsapp_number')(selectedClient.phone); }}>
                <span className="px-0.5 text-[11px] text-[#787774] dark:text-gray-400">Uguale al telefono</span>
              </RowAction>
            )}
          </>
        }
      />

      {/* Sito web sempre visibile: vuoto non compariva e non si capiva dove aggiungerlo */}
      <InlineTextRow
        icon={rowIcon(Globe)} label="Sito web" value={selectedClient.website} isLink
        onSave={saveField('website')} canEdit={canEdit}
        actions={selectedClient.website ? (
          <>
            <RowAction title="Apri sito" onClick={() => window.open(selectedClient.website.startsWith('http') ? selectedClient.website : `https://${selectedClient.website}`, '_blank')}>
              <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
            </RowAction>
            {copyAction(selectedClient.website, 'Sito web')}
          </>
        ) : undefined}
      />

      {showVoiceRow && (
        <div>
          <ValueRow icon={rowIcon(PhoneCall)} label="Chiamate AI" onClick={() => setVoiceOpen((v) => !v)}>
            <span className="flex items-center gap-1 text-[rgba(55,53,47,0.6)] dark:text-gray-400">
              Storico e chiamata
              {voiceOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            </span>
          </ValueRow>
          {voiceOpen && (
            <div className="ml-1">
              <VoiceCallCard leadId={selectedClient.id} canDial={!!canDialVoice} />
            </div>
          )}
        </div>
      )}

      <ValueRow
        icon={rowIcon(MapPin)} label="Indirizzo" value={address || null}
        onClick={address ? () => handleCopy(address, 'Indirizzo') : onEditWizard}
        actions={copyAction(address, 'Indirizzo')}
      />

      <InlineTextRow
        icon={rowIcon(CreditCard)} label="P.IVA" value={selectedClient.vat_number}
        onSave={saveField('vat_number')} canEdit={canEdit}
        actions={copyAction(selectedClient.vat_number, 'P.IVA')}
      />

      {showAll && hiddenFields.map((f) => <React.Fragment key={f.key}>{f.render()}</React.Fragment>)}

      {hiddenFields.length > 0 && (
        <button
          type="button"
          onClick={toggleShowAll}
          className="mt-0.5 flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left text-[12px] text-[#787774] hover:bg-[rgba(55,53,47,0.08)] dark:text-gray-400 dark:hover:bg-white/[0.06] transition-colors"
        >
          {showAll ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          {showAll ? 'Mostra meno' : `Mostra tutto (${hiddenFields.length} ${hiddenFields.length === 1 ? 'campo' : 'campi'})`}
        </button>
      )}
    </PanelSection>
  );
}
